import { FormEvent, useEffect, useRef, useState } from "react";
import { LuGift, LuLoader2 } from "react-icons/lu";
import apiClient from "../../../api-services/ApiClient";
import { inputBase } from "../../../app-components/ui/styles";
import { getErrorMessage } from "../../../utils";
import notify from "../../../utils/notify";

const PLANS = [
  { value: "STANDARD", label: "Standard" },
  { value: "BRONZE", label: "Bronze" },
  { value: "SILVER", label: "Silver" },
  { value: "GOLD", label: "Gold" },
  { value: "ELITEGOLD", label: "Elite Gold" },
];
const LENGTHS = [
  { value: 1, label: "1 month" },
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "12 months" },
];

type Props = {
  member: { id: number; name: string } | null;
  onClose: () => void;
  onGranted: () => void;
};

/**
 * Complimentary subscription for a member. A reason is required; the grant is
 * recorded (who, when, why) and shows in Transactions as "Complimentary" (₦0,
 * never counted as revenue). If the member is already subscribed, it extends
 * from the end of their current subscription.
 */
const GrantSubscriptionDialog = ({ member, onClose, onGranted }: Props) => {
  const [plan, setPlan] = useState("GOLD");
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const firstField = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!member) return;
    setPlan("GOLD");
    setMonths(1);
    setReason("");
    setError("");
    firstField.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  if (!member) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      setError('Give a short reason (at least 10 characters), e.g. "Compensation for failed payment on 3 Nov".');
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res: any = await apiClient.post(`elite-users/${member.id}/grant-subscription`, {
        plan,
        duration_months: months,
        reason: reason.trim(),
      });
      notify.success(res?.data?.message ?? `${member.name} was granted a subscription.`);
      onGranted();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div aria-hidden className="absolute inset-0 bg-black/40" onClick={() => !saving && onClose()} />
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="grant-title"
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-pop"
      >
        <div className="mb-5 flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hc_F4EBFA text-honeConnectPurple">
            <LuGift className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 id="grant-title" className="text-base font-semibold text-honeConnectBlack">
              Grant a subscription to {member.name}
            </h2>
            <p className="mt-1 text-sm text-honeConnectGray">
              Complimentary, at no charge. It's recorded with your name and reason, shows in Transactions as
              "Complimentary", and extends any subscription they already have.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-medium text-hcMuted">
            Plan
            <select ref={firstField} value={plan} onChange={(e) => setPlan(e.target.value)} className={`${inputBase} mt-1 w-full py-2`}>
              {PLANS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-hcMuted">
            Length
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className={`${inputBase} mt-1 w-full py-2`}>
              {LENGTHS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-3 block text-xs font-medium text-hcMuted">
          Reason (required)
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            rows={3}
            maxLength={500}
            placeholder="e.g. Compensation for failed payment on 3 Nov"
            className={`${inputBase} mt-1 w-full resize-none py-2`}
          />
        </label>
        {error && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-10 rounded-lg border border-hcBorder bg-white px-4 text-sm font-medium text-honeConnectBlack hover:bg-hcBg disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-honeConnectPurple px-4 text-sm font-semibold text-white hover:bg-hc_59077A disabled:opacity-70"
          >
            {saving && <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Grant subscription
          </button>
        </div>
      </form>
    </div>
  );
};

export default GrantSubscriptionDialog;
